import {
  Box, Typography, Paper, Stack, Button, Chip, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Grid2 as Grid, CircularProgress, Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { salesInvoicesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

const REASON_LABEL: Record<string, string> = {
  OutOfStock: "Out of stock",
  CheaperEquivalent: "Cheaper equivalent",
  PatientPreference: "Patient preference",
  Therapeutic: "Therapeutic substitute",
  Generic: "Generic equivalent",
};

export function SalesInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["sales-invoice", id],
    queryFn: () => salesInvoicesApi.get(id!),
    enabled: !!id,
  });

  if (query.isLoading) return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  if (query.isError) return <Alert severity="error">{errorMessage(query.error)}</Alert>;
  if (!query.data) return null;

  // Backend returns the detail as a loose shape; assume the standard structure
  const si = query.data as {
    id: string; documentNo: string; documentDate: string; status: string; billType: string;
    customerId?: string; customerName?: string; customerPhone?: string;
    subTotal: number; taxAmount: number; discountAmount: number; netAmount: number;
    journalEntryId?: string; notes?: string;
    lines: Array<{
      id: string; lineNo: number; lineType: string;
      itemId?: string; itemName?: string; sku?: string;
      serviceId?: string; serviceName?: string;
      description?: string; prescriptionRef?: string;
      substitutedFromItemId?: string; substitutedFromItemName?: string; substituteReasonCode?: string;
      qty: number; rate: number; taxAmount: number; discountAmount: number; lineTotal: number;
    }>;
    payments: Array<{
      id: string; mode: string; amountReceived: number; reference?: string; paidAt: string;
    }>;
  };

  const totalReceived = si.payments.reduce((s, p) => s + p.amountReceived, 0);
  const balanceDue = Math.max(0, si.netAmount - totalReceived);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/sales/invoices")}>
          All invoices
        </Button>
        <Typography variant="h5" fontWeight={600}>{si.documentNo}</Typography>
        <Chip label={si.status} color={STATUS_COLOR[si.status] ?? "default"} />
        <Chip label={si.billType} variant="outlined" />
        {si.journalEntryId && (
          <Tooltip title="Posted to ledger">
            <Chip icon={<ReceiptLongIcon />} label="GL posted" color="success" variant="outlined" />
          </Tooltip>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Customer</Typography>
            <Typography>
              {si.customerName ?? "—"}
              {si.customerPhone ? ` · ${si.customerPhone}` : ""}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Bill date</Typography>
            <Typography>{new Date(si.documentDate).toLocaleDateString()}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Sub-total</Typography>
            <Typography>{inr.format(si.subTotal)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Tax</Typography>
            <Typography>{inr.format(si.taxAmount)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Net</Typography>
            <Typography variant="h6" color="primary">{inr.format(si.netAmount)}</Typography>
          </Grid>
          {si.notes && (
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography sx={{ whiteSpace: "pre-line" }}>{si.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>#</TableCell>
              <TableCell sx={{ width: 100 }}>Type</TableCell>
              <TableCell>Item / Service</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Tax</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Line total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {si.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>{l.lineNo}</TableCell>
                <TableCell><Chip size="small" label={l.lineType} variant="outlined" /></TableCell>
                <TableCell>
                  <Stack>
                    <strong>{l.sku ?? l.serviceName ?? "—"}</strong>
                    <span>{l.itemName ?? l.serviceName ?? l.description ?? ""}</span>
                    {l.prescriptionRef && (
                      <Typography variant="caption" color="text.secondary">Rx ref: {l.prescriptionRef}</Typography>
                    )}
                    {l.substitutedFromItemId && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <SwapHorizIcon fontSize="small" color="warning" />
                        <Typography variant="caption" color="warning.main">
                          Substituted from {l.substitutedFromItemName ?? "another item"}
                          {l.substituteReasonCode ? ` — ${REASON_LABEL[l.substituteReasonCode] ?? l.substituteReasonCode}` : ""}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{l.qty}</TableCell>
                <TableCell align="right">{inr.format(l.rate)}</TableCell>
                <TableCell align="right">{inr.format(l.taxAmount)}</TableCell>
                <TableCell align="right">{inr.format(l.discountAmount)}</TableCell>
                <TableCell align="right">{inr.format(l.lineTotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Payments</Typography>
        <Divider sx={{ mb: 1 }} />
        {si.payments.length === 0 && <Alert severity="info">No payments recorded yet.</Alert>}
        {si.payments.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 160 }}>Mode</TableCell>
                <TableCell align="right" sx={{ width: 150 }}>Amount</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell sx={{ width: 180 }}>Paid at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {si.payments.map(p => (
                <TableRow key={p.id}>
                  <TableCell><Chip size="small" label={p.mode} variant="outlined" /></TableCell>
                  <TableCell align="right">{inr.format(p.amountReceived)}</TableCell>
                  <TableCell>{p.reference ?? "—"}</TableCell>
                  <TableCell>{p.paidAt ? new Date(p.paidAt).toLocaleString() : "—"}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><strong>Total received</strong></TableCell>
                <TableCell align="right"><strong>{inr.format(totalReceived)}</strong></TableCell>
                <TableCell colSpan={2} />
              </TableRow>
              <TableRow>
                <TableCell><strong>Balance due</strong></TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" color={balanceDue > 0 ? "error" : "success.main"}>
                    {inr.format(balanceDue)}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
